// src/cd/pages/CastingDirectorSettlements.js
import React, { useState, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Card, Badge, Spinner, Row, Col, Breadcrumb } from 'react-bootstrap';
import { 
    Search, Calendar, Download, Eye, ShieldAlert, CheckCircle, Clock, 
    XCircle, ArrowLeft, CreditCard, DollarSign, FileText, Printer 
} from 'lucide-react';

import { getCDSettlements, getCDSettlementDetails } from '../../services/adminApi';
// import '../pages/AdminPaymentsLog.css'; // Uses your existing theme styling

const CastingDirectorSettlements = () => {
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'detail'
    const [selectedId, setSelectedId] = useState(null);

    // List View States
    const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Statuses');
    const [typeFilter, setTypeFilter] = useState('All Types');

    // Detail View States
    const [detailPayload, setDetailPayload] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Fetch Settlements List
    const fetchSettlements = async () => {
        setLoading(true);
        try {
            const params = {
                search: search,
                status: statusFilter === 'All Statuses' ? undefined : statusFilter,
                type: typeFilter === 'All Types' ? undefined : typeFilter
            };
            const response = await getCDSettlements(params);
            setSettlements(response.data.data || []);
        } catch (err) {
            console.error('Could not load settlements.', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchSettlements();
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [search, statusFilter, typeFilter]);

    // Fetch Detail View Data
    const handleViewDetail = async (id) => {
        setSelectedId(id);
        setViewMode('detail');
        setDetailLoading(true);
        try {
            const response = await getCDSettlementDetails(id);
            setDetailPayload(response.data.data);
        } catch (err) {
            console.error('Could not load settlement details.', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const cleanStatus = (status || '').toUpperCase();
        if (cleanStatus === 'PAID' || cleanStatus === 'COMPLETED') {
            return <Badge bg="success" className="px-2 py-1"><CheckCircle size={12} className="me-1"/>{status}</Badge>;
        }
        if (cleanStatus === 'PROCESSING') {
            return <Badge bg="primary" className="px-2 py-1"><Clock size={12} className="me-1"/>{status}</Badge>;
        }
        if (cleanStatus === 'INITIATED') {
            return <Badge bg="warning" text="dark" className="px-2 py-1"><Clock size={12} className="me-1"/>{status}</Badge>;
        }
        return <Badge bg="danger" className="px-2 py-1"><XCircle size={12} className="me-1"/>{status}</Badge>;
    };

    /* =========================================================
       SCREEN 2: SETTLEMENT DETAIL VIEW (READ ONLY FOR CD)
    ========================================================= */
    if (viewMode === 'detail') {
        const item = detailPayload?.settlement;
        const txs = detailPayload?.transactions || [];
        const isPaid = item?.status === 'Paid' || item?.status === 'Completed' || item?.status === 'PAID';

        return (
            <div className="container-fluid py-4 admin-payments-container">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <Button variant="outline-secondary" className="btn-sm admin-payments-btn-secondary mb-2" onClick={() => setViewMode('list')}>
                            <ArrowLeft size={16} className="me-1" /> Back to My Settlements
                        </Button>
                        <h2 className="admin-payments-title">Settlement Details</h2>
                        <p className="admin-payments-subtitle">Reference ID: <span className="font-monospace text-white">{item?.settlement_code}</span></p>
                    </div>
                    <Button variant="outline-secondary" className="btn-sm admin-payments-btn-secondary" onClick={() => window.print()}>
                        <Printer size={16} className="me-1" /> Print / Export
                    </Button>
                </div>

                {detailLoading || !item ? (
                    <div className="text-center p-5"><Spinner animation="border" style={{ color: '#ffffff' }} /></div>
                ) : (
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 rounded-4 admin-payments-card h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                        <CreditCard size={20} className="text-danger" />
                                        <h5 className="fw-bold m-0 text-white">Settlement Summary</h5>
                                    </div>
                                    <Row className="g-3 text-white" style={{ fontSize: '0.9rem' }}>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Settlement ID</span>
                                            <strong className="text-primary font-monospace">{item.settlement_code}</strong>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Settlement Type</span>
                                            <strong>{item.settlement_type}</strong>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Project / Period</span>
                                            <strong>{item.settlement_period}</strong>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Status</span>
                                            <div>{getStatusBadge(item.status)}</div>
                                        </Col>
                                    </Row>
                                    <hr style={{ borderColor: '#2D3142' }} />
                                    <Row className="g-3 text-white">
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Total Transactions</span>
                                            <h5 className="mb-0 fw-bold">{item.total_transactions}</h5>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Total Gross Amount</span>
                                            <h5 className="mb-0 fw-bold">₹{Number(item.gross_amount).toFixed(2)}</h5>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Paid Amount</span>
                                            <h5 className="mb-0 fw-bold text-success">₹{Number(item.paid_amount || 0).toFixed(2)}</h5>
                                        </Col>
                                        <Col md={3}>
                                            <span className="admin-payments-table-text-muted d-block">Net Payable</span>
                                            <h5 className="mb-0 fw-bold text-info">₹{Number(item.net_payable_amount || item.gross_amount).toFixed(2)}</h5>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 rounded-4 admin-payments-card h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                        <DollarSign size={20} className="text-danger" />
                                        <h5 className="fw-bold m-0 text-white">Payment Receipt Details</h5>
                                    </div>
                                    <div className="text-white mb-2" style={{ fontSize: '0.9rem' }}>
                                        <span className="admin-payments-table-text-muted d-block">UTR / Bank Reference</span>
                                        <strong className="font-monospace text-warning">{item.utr_number || '—'}</strong>
                                    </div>
                                    <div className="text-white mb-2" style={{ fontSize: '0.9rem' }}>
                                        <span className="admin-payments-table-text-muted d-block">Payment Mode</span>
                                        <strong>{item.payment_mode || 'Bank Transfer'}</strong>
                                    </div>
                                    <div className="text-white mb-3" style={{ fontSize: '0.9rem' }}>
                                        <span className="admin-payments-table-text-muted d-block">Payment Date</span>
                                        <strong>{item.payment_date ? new Date(item.payment_date).toLocaleDateString('en-IN') : '—'}</strong>
                                    </div>
                                    <div className="d-grid gap-2 mt-4">
                                        <Button variant="outline-primary" size="sm" onClick={() => window.print()}>
                                            <Download size={14} className="me-1" /> Download Statement
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" disabled={!isPaid} onClick={() => window.print()}>
                                            <FileText size={14} className="me-1" /> Download Tax Invoice
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={12}>
                            <Card className="border-0 rounded-4 admin-payments-card">
                                <Card.Body className="p-4">
                                    <h5 className="fw-bold mb-3 text-white">Settlement Transactions</h5>
                                    <div className="table-responsive">
                                        <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless admin-payments-table">
                                            <thead className="admin-payments-thead">
                                                <tr className="admin-payments-thead-tr">
                                                    <th>Order ID</th>
                                                    <th>Project</th>
                                                    <th>Type</th>
                                                    <th>Date</th>
                                                    <th>Gross Amount</th>
                                                    <th>Commission</th>
                                                    <th>Net Payable</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {txs.map((tx, idx) => (
                                                    <tr key={idx} className="admin-payments-tbody-tr">
                                                        <td className="fw-bold text-white font-monospace">{tx.id}</td>
                                                        <td className="text-white">{tx.project}</td>
                                                        <td className="admin-payments-text-secondary">{tx.type}</td>
                                                        <td className="admin-payments-text-secondary">{tx.date}</td>
                                                        <td className="text-white fw-semibold">₹{tx.gross.toFixed(2)}</td>
                                                        <td className="text-danger">-₹{tx.commission.toFixed(2)}</td>
                                                        <td className="text-success fw-bold">₹{tx.net.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        );
    }

    /* =========================================================
       SCREEN 1: SETTLEMENT LIST VIEW
  ========================================================= */
    return (
        <div className="container-fluid py-4 admin-payments-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="admin-payments-title">My Settlements</h2>
                    <p className="admin-payments-subtitle">View your settlement history, breakdown, and payment receipts</p>
                </div>
                <Badge bg="primary" className="px-3 py-2 fs-6 rounded-pill">Total Settlements: {settlements.length}</Badge>
            </div>

            <Card className="border-0 rounded-4 overflow-hidden mb-4 admin-payments-card">
                <Card.Body className="p-4 bg-transparent admin-payments-border-bottom">
                    <Row className="g-3">
                        <Col lg={4} md={6}>
                            <InputGroup className="border-0 rounded-3 admin-payments-input-group">
                                <InputGroup.Text className="bg-transparent border-0 text-secondary">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search Settlement ID, Period, UTR..."
                                    className="bg-transparent border-0 shadow-none ps-0 admin-payments-input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col lg={3} md={3}>
                            <Form.Select 
                                className="border-0 rounded-3 text-white py-2 admin-payments-select" 
                                value={statusFilter} 
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="All Statuses">All Statuses</option>
                                <option value="Initiated">Initiated</option>
                                <option value="Processing">Processing</option>
                                <option value="Completed">Completed</option>
                                <option value="Paid">Paid</option>
                            </Form.Select>
                        </Col>
                        <Col lg={3} md={3}>
                            <Form.Select 
                                className="border-0 rounded-3 text-white py-2 admin-payments-select" 
                                value={typeFilter} 
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="All Types">All Types</option>
                                <option value="Per Project">Per Project</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Monthly">Monthly</option>
                            </Form.Select>
                        </Col>
                        <Col lg={2} md={6} className="text-end">
                            <Button variant="outline-secondary" className="admin-payments-btn-secondary w-100" onClick={() => window.print()}>
                                <Download size={18} className="me-1" /> Export
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>

                <div className="table-responsive">
                    <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless admin-payments-table">
                        <thead className="admin-payments-thead">
                            <tr className="admin-payments-thead-tr">
                                <th className="px-4 py-3 text-uppercase">#</th>
                                <th className="py-3 text-uppercase">Settlement ID</th>
                                <th className="py-3 text-uppercase">Type</th>
                                <th className="py-3 text-uppercase">Project / Period</th>
                                <th className="py-3 text-uppercase">Transactions</th>
                                <th className="py-3 text-uppercase">Gross Amount</th>
                                <th className="py-3 text-uppercase">Paid Amount</th>
                                <th className="py-3 text-uppercase">Status</th>
                                <th className="py-3 text-uppercase">Payment Date</th>
                                <th className="px-4 py-3 text-center text-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.9rem' }}>
                            {loading ? (
                                <tr>
                                    <td colSpan="10" className="text-center p-5 admin-payments-text-secondary">
                                        <Spinner animation="border" className="me-2" size="sm" /> Loading Settlements...
                                    </td>
                                </tr>
                            ) : settlements.length > 0 ? (
                                settlements.map((item, idx) => (
                                    <tr key={item.id} className="admin-payments-tbody-tr">
                                        <td className="px-4 admin-payments-text-secondary">{idx + 1}</td>
                                        <td className="fw-semibold text-white font-monospace">{item.settlement_code}</td>
                                        <td className="text-white">{item.settlement_type}</td>
                                        <td className="admin-payments-text-secondary">{item.settlement_period}</td>
                                        <td className="text-white fw-bold">{item.total_transactions}</td>
                                        <td className="fw-bold text-white">₹{Number(item.gross_amount).toFixed(2)}</td>
                                        <td className="fw-bold text-success">₹{Number(item.paid_amount || 0).toFixed(2)}</td>
                                        <td>{getStatusBadge(item.status)}</td>
                                        <td className="text-muted">{item.payment_date ? new Date(item.payment_date).toLocaleDateString('en-IN') : '-'}</td>
                                        <td className="px-4 text-center">
                                            <button
                                                type="button"
                                                style={{
                                                    backgroundColor: "#2563eb",
                                                    color: "#ffffff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    width: "32px",
                                                    height: "32px",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => handleViewDetail(item.id)}
                                                title="View Settlement Details"
                                            >
                                                <Eye size={15} color="#ffffff" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="text-center p-5 admin-payments-text-secondary">
                                        <ShieldAlert size={36} className="mb-2" />
                                        <p className="m-0">No settlement records found.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

export default CastingDirectorSettlements;